<!-- Component can be used in microscope and periscope -->
<template>
  <details ref="details">
    <summary ref="summaryElement">
      <slot name="activator" :open="open" :close="close" :toggle="toggle" />
    </summary>

    <div class="app-menu-content-wrapper">
      <div ref="menuContent" class="app-menu-content">
        <slot :close="close" />
      </div>
    </div>
  </details>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useDetectOutsideClick } from '../../composables'

const props = withDefaults(
  defineProps<{
    closeOnClick?: boolean
    disableY?: boolean
    disableX?: boolean
    offsetTop?: number
    offsetBottom?: number
    offsetRight?: number
    offsetLeft?: number
  }>(),
  {
    closeOnClick: true,
    offsetTop: 0,
    offsetBottom: 0,
    offsetRight: 0,
    offsetLeft: 0,
  },
)

const details = ref<HTMLElement | null>(null)
const menuContent = ref<HTMLElement | null>(null)
const summaryElement = ref<HTMLElement | null>(null)

const close = () => {
  details.value?.removeAttribute('open')
  removeEventListener('resize', repositionContent)
  removeEventListener('scroll', repositionContent, true)
}

const open = () => {
  details.value?.setAttribute('open', 'true')
  addEventListener('resize', repositionContent)
  // Scroll does not bubble; capture is needed to catch inner scroll containers.
  addEventListener('scroll', repositionContent, true)
  repositionContent()
}

const toggle = () => (details.value?.hasAttribute('open') ? close() : open())

const repositionContent = () => {
  if (!menuContent.value || !summaryElement.value) return

  const originRect = summaryElement.value.getBoundingClientRect()

  // För fixed positioning använder vi direkt left/top istället för transform
  let left = originRect.left + props.offsetLeft - props.offsetRight
  let top = originRect.bottom + props.offsetTop - props.offsetBottom

  // Kontrollera om menyn går utanför skärmen horisontellt
  menuContent.value.style.left = `${left}px`
  menuContent.value.style.top = `${top}px`

  // Efter att ha satt positionen, kolla om vi behöver justera
  const menuRect = menuContent.value.getBoundingClientRect()

  if (!props.disableX && menuRect.right > window.innerWidth) {
    left = originRect.right - menuRect.width - props.offsetLeft + props.offsetRight
    menuContent.value.style.left = `${left}px`
  }

  if (!props.disableY && menuRect.bottom > window.innerHeight) {
    top = originRect.top - menuRect.height - props.offsetTop + props.offsetBottom
    menuContent.value.style.top = `${top}px`
  }
}

useDetectOutsideClick(details, close)
</script>

<style lang="scss" scoped>
details {
  summary {
    cursor: pointer;
    list-style-type: none;

    &::-webkit-details-marker {
      display: none;
    }
  }

  .app-menu-content-wrapper {
    position: relative;
  }

  .app-menu-content {
    position: fixed;
    z-index: 1;
  }
}
</style>
